// src/components/Forum/Forum.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Home, FileText, Edit, Users, Bell, Search, ChevronDown, 

    ThumbsUp, MessageCircle, MessageSquare, Share2, MoreHorizontal, Pencil, Code, Briefcase, DollarSign, Plus, X, Loader

} from 'lucide-react';
import './Forum.css'; // Make sure this CSS file is correctly styled for the new elements

const API_URL = process.env.REACT_APP_API_URL;

// --- API Configuration ---
// This communicates with your backend. Assumes the admin token is in localStorage.
const api = axios.create({
    baseURL: `${API_URL}`,
    headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
    }
});

// Helper to format dates
const formatTimestamp = (dateString) => {
    const options = { month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
};

const forumTopics = [
    { name: 'Software & Apps', icon: Code },
    { name: 'Immigration Q&A', icon: Briefcase },
    { name: 'Finance', icon: DollarSign },
    { name: 'Student Life', icon: Pencil },
];

// --- New Post Modal Component ---
const CreatePostModal = ({ isOpen, onClose, onCreatePost }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!title.trim() || !content.trim()) {
            alert('Title and content cannot be empty.');
            return;
        }
        onCreatePost({ title, content });
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h3>Create a New Post</h3>
                    <button onClick={onClose} className="close-button"><X size={24} /></button>
                </div>
                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-group">
                        <label htmlFor="post-title">Title</label>
                        <input
                            id="post-title"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Enter a concise title for your post"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="post-content">Content</label>
                        <textarea
                            id="post-content"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            rows="8"
                            placeholder="What do you want to ask or share?"
                        ></textarea>
                    </div>
                    <div className="form-actions">
                        <button type="button" onClick={onClose} className="cancel-btn">Cancel</button>
                        <button type="submit" className="submit-btn">Post</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


const Forum = () => {
    // --- State Management ---
    const [posts, setPosts] = useState([]);
    const [users, setUsers] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    // --- Data Fetching Effect ---
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError('');
                // Fetch all posts from the backend
                const postsRes = await api.get('/forum/posts');
                const fetchedPosts = postsRes.data;

                // Gather unique author IDs from posts and comments to fetch user data
                const authorIds = new Set();
                fetchedPosts.forEach(post => {
                    if (post.authorId) authorIds.add(post.authorId);
                    post.comments?.forEach(comment => {
                        if (comment.authorId) authorIds.add(comment.authorId);
                    });
                });

                // Fetch user data for all authors in a single batch
                const userPromises = [...authorIds].map(id => api.get(`/admin/users/${id}`).catch(() => null));
                const userResults = await Promise.all(userPromises);
                
                const usersMap = {};
                userResults.filter(Boolean).forEach(res => {
                    if (res.data && res.data.userId) {
                        usersMap[res.data.userId] = res.data;
                    }
                });

                setUsers(usersMap);
                setPosts(fetchedPosts);
            } catch (err) {
                console.error("Failed to fetch forum data:", err);
                setError("Could not load forum data. Please check your connection and try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // --- Post Creation Handler ---
    const handleCreatePost = async (postData) => {
        try {
            // Send the new post to the backend
            const response = await api.post('/forum/posts', postData);
            const newPost = response.data;
            
            // Add the new post to the top of the list for instant UI update
            setPosts(prevPosts => [newPost, ...prevPosts]);
            setIsModalOpen(false); // Close the modal
            alert('Post created successfully!');
        } catch (err) {
            console.error('Failed to create post:', err);
            alert('Error: Could not create the post. Please try again.');
        }
    };

    return (
        <div className="forum-page-wrapper">
            {/* The ForumHeader component remains mostly presentational */}
            <header className="forum-header-bar">
              <div className="header-content">
                <div className="header-nav-icons">
                  <div className="header-nav-icon active"><Home size={24} strokeWidth={2.5}/></div>
                  <div className="header-nav-icon"><FileText size={24} /></div>
                </div>
                <div className="header-search-container">
                  <Search size={18} className="header-search-icon" />
                  <input type="text" placeholder="Search Forum" className="header-search-input" />
                </div>
                <div className="header-user-actions">
                  <button onClick={() => setIsModalOpen(true)} className="new-post-btn">
                    New Post <Plus size={20} />
                  </button>
                </div>
              </div>
            </header>

            <main className="forum-container">
                {/* --- Left Sidebar --- */}
                <aside className="forum-sidebar-left">
                    <button className="create-space-btn"><Plus size={20} /> Create Space</button>
                    <div className="topic-list">
                        {forumTopics.map(topic => {
                            const Icon = topic.icon;
                            return (
                                <div className="topic-item" key={topic.name}><Icon size={20} /><span>{topic.name}</span></div>
                            );
                        })}
                    </div>
                </aside>

                {/* --- Center Feed --- */}
                <section className="forum-feed">
                    {/* This card now opens the modal to create a real post */}
                    <div className="post-card create-post-card" onClick={() => setIsModalOpen(true)}>
                        <div className="create-post-header">
                            <div className="user-avatar-initials">A</div>
                            <div className="create-post-input-fake">What do you want to ask or share?</div>
                        </div>
                        <div className="create-post-actions">
                            <button>Ask</button>
                            <button>Answer</button>
                            <button>Post</button>
                        </div>
                    </div>
                    
                    {/* Display Loading, Error, or Post Content */}
                    {loading && <div className="loading-state"><Loader className="spinner" size={48}/> <p>Loading posts...</p></div>}
                    {error && <div className="error-state"><p>{error}</p></div>}
                    {!loading && !error && posts.map((post) => {
                        const author = users[post.authorId];
                        return (
                            <div key={post._id} className="post-card">
                                <div className="post-header">
                                    <div className="user-avatar-initials">{author?.fullName?.charAt(0) || 'U'}</div>
                                    <div className="post-author-info">
                                        <div>
                                            <span className="post-author-name">{author?.fullName || 'Unknown User'}</span>
                                            <span className="post-author-follow"> · Follow</span>
                                        </div>
                                        <p className="post-timestamp">{author?.email || 'No email'} · {formatTimestamp(post.createdAt)}</p>
                                    </div>
                                    <MoreHorizontal className="post-options-icon" size={24} />
                                </div>
                                <div className="post-content">
                                    <h4>{post.title}</h4>
                                    <p>{post.content}</p>
                                    {post.attachments?.[0]?.fileUrl && <img src={post.attachments[0].fileUrl} alt="Post content" className="post-image" />}
                                </div>
                                <div className="post-actions">
                                    <div className="action-group">
                                        <button className="action-button upvote-button">
                                            <ThumbsUp size={18} />
                                            Upvote · {post.reactions?.length || 0}
                                        </button>
                                    </div>
                                    <div className="action-button-icon">
                                        <MessageCircle size={20} />
                                        <span>{post.comments?.length || 0}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </section>
                
                {/* --- Right Sidebar --- */}
                <aside className="forum-sidebar-right">
                    <div className="widget-card">
                        <h4 className="widget-title">Featured Content</h4>
                        <p className="widget-content">Check out our new guide on student visa applications.</p>
                    </div>
                </aside>
            </main>

            {/* --- Render the Modal for creating posts --- */}
            <CreatePostModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onCreatePost={handleCreatePost}
            />
        </div>
    );
};

export default Forum;